/**
 * Composition root for compiled macro packages: grant loader, raw-upload bridge, native package-run bridge,
 * session coordinator and capture relay, all fed from the real main-process IPC stream.
 */
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { ConnectedHost } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type { IMessageRouter } from '../../services/MessageRouter';
import { MacroPackageSocketLoader, RawPackageUploadSocketBridge } from '../../services/playjector/MacroPackageSocketLoader';
import { NativeMacroPackageBridge } from '../../services/playjector/NativeMacroPackageBridge';
import { MacroPackageCaptureRelay } from '../local-host-session/MacroPackageCaptureRelay';
import type { LocalHostPackageRuns } from '../local-host-session/LocalHostSessionCoordinator';
import { MacroSessionCoordinator } from './MacroSessionCoordinator';
import { MacroPackageIpcRouter } from './MacroPackageIpcRouter';
import { MacroPackageVerificationRelay } from '../../services/playjector/MacroPackageVerificationRelay';
import { LocalOwnedNativeActionRouter } from '../local-host-session/LocalOwnedNativeActionRouter';

export interface MacroPackageRuntimeDeps {
  ipc: IIpcService;
  realtime: Pick<IRealtimeService, 'sendMessage'> & Partial<Pick<IRealtimeService, 'trySendMessage' | 'onDisconnected' | 'isConnected'>>;
  realtimeRouter: IMessageRouter;
  /** Subscribes to main-process IPC (production: `ipcReceive`, a listener on the `mainMessage` channel). */
  receive: (listener: (message: PlaycastMessage<PlaycastMessageTarget>) => void) => void;
  /** Subscribes to private macro-package envelopes that cannot pass the public message validator. */
  receiveMacroPackages?: (listener: (message: PlaycastMessage<PlaycastMessageTarget>) => void) => void;
  ids?: () => string;
  now?: () => number;
  ownsVerificationHost?: (host: ConnectedHost) => boolean;
}
export interface MacroPackageRuntime {
  loader: MacroPackageSocketLoader;
  uploads: RawPackageUploadSocketBridge;
  bridge: NativeMacroPackageBridge;
  sessions: MacroSessionCoordinator;
  capture: MacroPackageCaptureRelay;
  router: MacroPackageIpcRouter;
  verification: MacroPackageVerificationRelay;
  /** Wiring for `LocalHostSessionCoordinator`. */
  localHostPackageRuns: LocalHostPackageRuns;
}

export function initializeMacroPackages(deps: MacroPackageRuntimeDeps): MacroPackageRuntime {
  const loader = new MacroPackageSocketLoader(deps.realtime, deps.realtimeRouter);
  const uploads = new RawPackageUploadSocketBridge(deps.ipc, deps.realtime, deps.realtimeRouter);
  const bridge = new NativeMacroPackageBridge((message) => deps.ipc.send(message));
  const verification = new MacroPackageVerificationRelay({ native: bridge, owns: deps.ownsVerificationHost ?? (() => false),
    realtime: { trySendMessage: message => deps.realtime.trySendMessage?.(message) ?? false,
      onDisconnected: listener => deps.realtime.onDisconnected?.(listener) ?? (() => undefined),
      isConnected: () => deps.realtime.isConnected?.() ?? false } }, deps.realtimeRouter);
  const sessions = new MacroSessionCoordinator({ loader, bridge, ids: deps.ids ?? (() => crypto.randomUUID()), now: deps.now ?? Date.now });
  // Each recording pins its mode at `start` (local editor → local, remote admin peer → campaign upload).
  const capture = new MacroPackageCaptureRelay({ ipc: deps.ipc, uploader: uploads, mode: () => 'local' });
  const router = new MacroPackageIpcRouter({ capture, bridge, uploads });
  const receive = (message: PlaycastMessage<PlaycastMessageTarget>) => router.receive(message);
  deps.receive(receive);
  deps.receiveMacroPackages?.(receive);
  return { loader, uploads, bridge, sessions, capture, router, verification,
    localHostPackageRuns: { bridge, ownedNative: new LocalOwnedNativeActionRouter(bridge), sessions, uploader: uploads, capture,
      claims: (message) => router.claims(message) } };
}
